import {
    addManifestPathsToProperties,
    getInsertableForEntitySet,
    getPageManifestPath,
    initializeCreationModeSchema,
    type LineItemDataForSchema
} from '../../utils';
import {
    addCustomColumnDefinition,
    addFragmentEnumForAnchor,
    addLineItemsTypeToSchema,
    addEnumForActionAnchor,
    addHeaderFooterCustomActionDefinition,
    addCustomFilterFieldDefinition,
    addEnumForEnableMassEdit
} from '../../utils';

import {
    ArtifactType,
    UIVOCABULARY,
    TemplateType,
    Visualization,
    SchemaKeyName,
    PropertyName,
    DefinitionName,
    FacetBase,
    SchemaTag,
    LogSeverity,
    UIVOCABULARYDOT,
    UIVOCABULARYALPHADOT,
    PageTypeV4,
    v4
} from '@sap/ux-specification-types';
import type { ExtensionLogger, GenerateAppSchemaParameters, Manifest } from '@sap/ux-specification-types';
import type { AnnotationTerm, EntitySet, EntityType, ConvertedMetadata } from '@sap-ux/vocabularies-types';
import type { DataFieldAbstractTypes, LineItem, SelectionFields } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import i18next from 'i18next';
import { log } from '../../../../extensionLogger';
import {
    createAnnotationPath,
    DEFINITION_LINK_PREFIX,
    determineDataType,
    getDataFieldDescription,
    getLabelForPropertyPath,
    prepareRef,
    parseSchemaDefinition,
    addDefinitionRef,
    getFacetDefinitionLink,
    updatePropertyIndices,
    replaceNamespaces,
    addDescriptiveEnumDefinition,
    addMessageToSchema,
    getFacetDefinitionKey,
    type ManifestPathParams
} from '../../../common';
import type { Definition } from 'typescript-json-schema';
import type {
    SapUi5MultiViewPaths,
    SapUiAppPageV4
} from '@sap/ux-specification-types/src/v4/webapp/manifest/sapUiPageV4';
import { configureCustomActionDefinition } from '../actions';
import type { App } from '../../sync-rules';
import { AppSchema, createApp, Page } from '../../sync-rules';
import { Decorator } from '../../../common/decoration/Decorator';
import type { DecoratorContext } from '../../../common/decoration/interfaces';
import type { PageContext } from '../../../common/decoration';
import {
    addManifestPathsToDefinitionPropertiesLR,
    FilterBar,
    GeneralListReportSettings,
    TableAndViews
} from './ListReportUtils';
import {
    CustomHeaderAction,
    Header,
    HeaderActions,
    CustomFilterFieldPosition,
    ViewToolBarAction,
    ALPChart,
    Table,
    TableCreationModeLR,
    AnalyticalConfiguration,
    ViewAnalyticalConfiguration
} from '../../export/controls';

/**
 * Parameter bag for post-generation schema adjustments.
 *
 * Used by helper functions that refine already generated schema nodes (e.g. ListReport/ALP tables and views),
 * including manifest-driven tweaks and custom extensions (custom actions / custom action menus).
 *
 * Conventions / contract:
 * - All adjustments mutate `appSchema` in-place.
 * - `tableDefinitionKey` and `facetDefinitionKey` must point to existing entries in `appSchema.definitions` that are being adjusted.
 * - `entityType` may be different from `generateParameters.entityType` for view-specific schemas; if provided, it should be used
 * - `entitySet` optionally scopes view-specific schema adjustments to a particular entity set (e.g. for multi-view scenarios).
 *   as the primary entity type (fallback is `generateParameters.entityType`).
 * - `viewKey` is only relevant for multi-view scenarios; if omitted, the default view is assumed.
 * - `sectionActionsDefinitionKey` / `scopedCustomActionDefinitionKey` / `customActionPosition` are only required when the
 *   adjustment step configures scoped custom actions and/or custom action menus.
 */
export interface SchemaAdjustmentParameters {
    appSchema: Definition;
    v4Page: v4.SapUiAppPageV4;
    tableDefinitionKey: string;
    logger: ExtensionLogger;
    facetDefinitionKey: string;
    viewKey?: string;
    entityType?: EntityType;
    entitySet?: EntitySet;
    navigationPath?: string;
    dataForLineItem: LineItemDataForSchema;
    sectionActionsDefinitionKey?: string;
    scopedCustomActionDefinitionKey?: string;
    customActionDefinitionName?: DefinitionName;
    sectionIdInManifest?: string;
    customActionPosition?: string;
}

/**
 * Default table type when not specified in manifest.
 */
export const DEFAULT_TABLE_TYPE = v4.TableTypeV4.ResponsiveTable;
const ROOT_PROPERTIES_ORDER: Array<keyof v4.ListReportConfigV4> = ['header', 'filterBar', 'chart', 'table'];
const UILINEITEM = '@UI.LineItem';

/**
 * Configuration interface for List Report pages.
 */
export interface ListReportPageConfig extends PageContext {
    isALP?: boolean;
    defaultAnnotation?: string;
    views?: v4.SapUi5Views;
}

/**
 * Type alias for a Page with ListReportPageConfig configuration.
 */
export type LRPage = Page<ListReportPageConfig>;

/**
 * Adds the selection fields to the app schema, as properties of the FilterBar.
 *
 * @param {EntityType} entityType - the entity type as part of the AVT ConvertedMetadata
 * @param {Definition} appSchema - the app specific schema that shall get enhanced
 * @param {AnnotationTerm<SelectionFields>} selectionFieldsAnnotation - the UI.SelectionField annotation
 * @param {Definition} [selectionFieldsDefinition] - Schema definitions of selection fields
 * @param {string} [qualifier] - Qualifier for SelectionFields
 * @returns {Definition} - the updated selection fields definition
 */
export function addSelectionFields(
    entityType: EntityType,
    appSchema: Definition,
    selectionFieldsAnnotation: AnnotationTerm<SelectionFields>,
    selectionFieldsDefinition?: Definition,
    qualifier?: string
): Definition {
    // No @validity check needed here

    if (!selectionFieldsDefinition) {
        selectionFieldsDefinition = appSchema.definitions[DefinitionName.SelectionFields] as Definition;
        selectionFieldsDefinition.additionalProperties = false;
        selectionFieldsDefinition.properties = {};
    }
    if (entityType) {
        selectionFieldsDefinition[SchemaTag.annotationPath] = createAnnotationPath(
            entityType.fullyQualifiedName,
            UIAnnotationTerms.SelectionFields,
            qualifier
        );
    }
    if (selectionFieldsAnnotation) {
        let i = 0;
        selectionFieldsAnnotation?.forEach((selectionField) => {
            const value = selectionField.value ?? `UnknownField${i}`;
            let description = selectionField.value;
            const fieldDefinition = 'SelectionField::' + prepareRef(value);
            if (selectionField.type === 'PropertyPath') {
                description = getLabelForPropertyPath(selectionField, entityType);
            }
            selectionFieldsDefinition.properties[fieldDefinition] = {
                $ref: DEFINITION_LINK_PREFIX + fieldDefinition
            };
            selectionFieldsDefinition.properties[fieldDefinition][SchemaTag.propertyIndex] = i;
            const dataType = determineDataType(selectionField);
            appSchema.definitions[fieldDefinition] = {
                type: 'object',
                properties: {},
                description,
                additionalProperties: false,
                ...(dataType && { dataType })
            };
            if (selectionField.value === undefined) {
                // Add message about unresolvable value for selection field
                addMessageToSchema(appSchema.definitions[fieldDefinition], {
                    text: i18next.t('UNRESOLVABLE_VALUE_OF_SELECTION_FIELD')
                });
            }
            appSchema.definitions[fieldDefinition][SchemaTag.isViewNode] = true;
            appSchema.definitions[fieldDefinition][SchemaTag.keys] = [{ name: SchemaKeyName.value, value: `${value}` }];
            if (selectionField.$target?.fullyQualifiedName.indexOf('@') > -1) {
                appSchema.definitions[fieldDefinition][SchemaTag.target] = selectionField.$target.fullyQualifiedName
                    .split('@')[0]
                    .split('/')[0];
            } else if (selectionField.$target?.fullyQualifiedName) {
                appSchema.definitions[fieldDefinition][SchemaTag.target] =
                    selectionField.$target.fullyQualifiedName.split('/')[0];
            }

            appSchema.definitions[fieldDefinition][SchemaTag.annotationType] = selectionField?.type;
            appSchema.definitions[fieldDefinition][SchemaTag.annotationPath] = `${
                selectionFieldsDefinition[SchemaTag.annotationPath]
            }/${i}`;
            i++;
        });
    }
    return selectionFieldsDefinition;
}

/**
 * Returns visual filters (if defined) from manifest page.
 *
 * @param v4Page - actual page in the manifest
 * @returns - The visual filters object if defined, otherwise undefined.
 */
export function getVisualFilters(v4Page: v4.SapUiAppPageV4): {} | undefined {
    const selectionFields =
        v4Page.options.settings?.controlConfiguration?.[`@${UIAnnotationTerms.SelectionFields}`]?.['filterFields'];
    if (selectionFields) {
        const visualFilters = {};
        Object.keys(selectionFields).forEach((field) => {
            if (!selectionFields[field].template) {
                visualFilters[field] = selectionFields[field];
            }
        });
        return Object.keys(visualFilters).length > 0 ? visualFilters : undefined;
    }
}

/**
 * Collects indexes of selection fields and entityProperties to sort visual filters.
 *
 * @param selectionFields - The selection fields annotation to be processed
 * @param entityType - the entity type as part of the AVT ConvertedMetadata
 * @returns - An array of property names sorted by their index.
 */
function getPropertiesIndex(selectionFields: AnnotationTerm<SelectionFields>, entityType: EntityType): string[] {
    const properties: string[] = [];
    selectionFields?.forEach((selectionField) => properties.push(selectionField.value));
    if (entityType) {
        entityType.entityProperties.forEach((property) =>
            !properties.includes(property.name) ? properties.push(property.name) : undefined
        );
    }
    return properties;
}

/**
 * Adds visual filters to the app schema, as properties of the FilterBar.
 *
 * @param {EntityType} entityType - the entity type as part of the AVT ConvertedMetadata
 * @param {Definition} appSchema - the app specific schema that shall get enhanced
 * @param visualFiltersManifest - the UI.SelectionFields under controlConfiguration
 * @param selectionFields - The selection fields annotation to be processed
 */
export function addVisualFilters(
    entityType: EntityType,
    appSchema: Definition,
    visualFiltersManifest: any,
    selectionFields: AnnotationTerm<SelectionFields>
) {
    // No @validity check needed here

    const propertiesIndex = getPropertiesIndex(selectionFields, entityType);
    const visualFilters = appSchema.definitions[DefinitionName.VisualFilters] as Definition;
    visualFilters.properties = {};
    for (const fieldName in visualFiltersManifest) {
        const filterField = visualFiltersManifest[fieldName];
        // No custom filter field support in visual filters
        if (!filterField.template) {
            const visualFiltersField = (visualFilters.properties[fieldName] = {
                $ref: getFacetDefinitionLink(DefinitionName.VisualFilter, fieldName)
            });
            const propertyIndex = propertiesIndex.indexOf(fieldName);
            if (propertyIndex !== -1) {
                visualFiltersField[SchemaTag.propertyIndex] = propertyIndex;
            }
            const filterDefinition = parseSchemaDefinition(DefinitionName.VisualFilter, fieldName, appSchema);
            if (filterField.visualFilter?.valueList) {
                filterDefinition[SchemaTag.annotationPath] = `${fieldName}/@${filterField.visualFilter.valueList}`;
            }
            const annotationLabel = entityType?.entityProperties
                ?.find((property) => property.name === fieldName)
                ?.annotations?.Common?.Label?.toString();
            filterDefinition.description = annotationLabel || fieldName;
            filterDefinition[SchemaTag.keys] = [{ name: SchemaKeyName.value, value: fieldName }];
        }
    }
}

/**
 * Method updates schema for custom filter fields:
 * 1. Enables creation of custom filter fields.
 * 2. Populates custom filter fields from manifest to schema.
 *
 * @param appSchema - the app specific schema that shall get enhanced
 * @param selectionFieldsDefinition - schema of selection fields which should contain custom filter fields
 * @param v4Page - actual page in the manifest
 */
export function applyCustomFilterFields(
    appSchema: Definition,
    selectionFieldsDefinition: Definition,
    v4Page: v4.SapUiAppPageV4
): void {
    selectionFieldsDefinition.additionalProperties = {
        $ref: `#/definitions/${DefinitionName.CustomFilterField}`
    };
    addCustomFilterFieldDefinition(appSchema, selectionFieldsDefinition, v4Page);
    const customSectionDefinition = appSchema.definitions?.[DefinitionName.CustomFilterFieldPosition] as Definition;

    // Apply decorators to the live definition (pass explicitly to avoid deep-copy in AppSchema constructor)
    new Decorator({ schema: new AppSchema(appSchema) }).init(
        CustomFilterFieldPosition,
        undefined,
        customSectionDefinition
    );

    if (typeof customSectionDefinition === 'object') {
        addDescriptiveEnumDefinition(
            appSchema,
            selectionFieldsDefinition,
            {
                schema: customSectionDefinition,
                property: 'anchor'
            },
            {
                resolveDefinition: true,
                keyProperties: [SchemaKeyName.value, SchemaKeyName.key],
                descriptionAsFallback: true
            }
        );
    }
}

/**
 *
 * @param appSchema - The app JSON schema to be updated
 * @param schemaIdForActions - The schema ID for the actions to be defined
 * @param annotations - The UI annotations for the given entity type
 * @param targetAnnotation - The target annotation for the actions
 * @param entityType - The entity type as part of the AVT ConvertedMetadata
 */
function defineActions(
    appSchema: Definition,
    schemaIdForActions: string,
    annotations,
    targetAnnotation: string,
    entityType: EntityType
) {
    appSchema.definitions[schemaIdForActions] = JSON.parse(JSON.stringify(appSchema.definitions['Actions']));
    const actions = appSchema.definitions[schemaIdForActions] as Definition;
    actions.properties = {};
    actions.additionalProperties = false;
    const chartAnnotation = annotations[targetAnnotation?.split(`${UIVOCABULARY}.`)[1]];
    if (chartAnnotation && chartAnnotation.Actions) {
        let i = 0;
        chartAnnotation.Actions.forEach((actionRecord) => {
            const actionDefinition = prepareRef(`${DefinitionName.ChartToolBarAction}<${actionRecord.Action}>`);
            const description = getDataFieldDescription(actionRecord as DataFieldAbstractTypes, entityType);
            const prefix = actionRecord.$Type.split(`${UIVOCABULARY}.`)[1];
            const action = (actions.properties[`${prefix}::${actionRecord.Action}`] = {
                $ref: DEFINITION_LINK_PREFIX + actionDefinition,
                description
            });
            //Add tags
            action[SchemaTag.propertyIndex] = i;
            action[SchemaTag.annotationType] = actionRecord.$Type;
            action[SchemaTag.annotationPath] = `/${actionRecord.fullyQualifiedName}`;
            //as ChartToolBarAction is an empty object, copy ViewToolBarAction and remove properties
            appSchema.definitions[actionDefinition] = JSON.parse(
                JSON.stringify(appSchema.definitions[DefinitionName.ViewToolBarAction])
            );

            // Apply decorators to the live definition (pass explicitly to avoid deep-copy in AppSchema constructor)
            const actionDef = appSchema.definitions[actionDefinition] as Definition;
            new Decorator({ schema: new AppSchema(appSchema) }).init(ViewToolBarAction, undefined, actionDef);

            (appSchema.definitions[actionDefinition] as Definition).properties = {};
            appSchema.definitions[actionDefinition][SchemaTag.annotationPath] = actionRecord.fullyQualifiedName;
            i++;
        });
    }
}

/**
 * Finds the right visualization for a given view.
 *
 * @param allVisualizations - list of visualizations for the given (S)PV
 * @param namespace - namespace for the UI annotations
 * @param viewKey - view key: primary, secondary, or anything else; may also be undefined. The first visualization is taken in this case.
 * @param visualizationType - allows to find the right visualization if there are many.
 * @returns the value of the first fitting visualization
 */
function findTheRightVisualization(
    allVisualizations: Array<object>,
    namespace: string,
    viewKey?: string,
    visualizationType?: Visualization
): { visualization?: string; targetAnnotation: string } {
    let visualization: string | undefined, visualizationObject: object, targetAnnotation: string;
    if (allVisualizations) {
        if (viewKey === 'primary') {
            visualizationObject = allVisualizations.find((vis) => {
                return vis['value'].indexOf('Chart') > -1;
            });
        } else if (viewKey === 'secondary') {
            visualizationObject = allVisualizations.find((vis) => {
                return vis['value'].indexOf(FacetBase.LineItem) > -1;
            });
        } else {
            if (visualizationType) {
                visualizationObject = allVisualizations.find((v) => v['value'].indexOf(visualizationType) > -1);
            } else {
                visualizationObject = allVisualizations[0];
            }
        }
        visualization =
            typeof visualizationObject?.['value'] === 'string'
                ? replaceNamespaces(visualizationObject['value'])
                : undefined;
        if (visualizationObject?.['$target']?.fullyQualifiedName) {
            targetAnnotation = `/${namespace}/${visualizationObject['$target'].fullyQualifiedName.split(namespace)[1]}`;
        } else if (visualization) {
            targetAnnotation = `/${namespace}/` + visualization.replace(`UI`, UIVOCABULARY);
        }
    }
    return { visualization, targetAnnotation };
}

/**
 * An annotationPath must be analyzed, to find the right target annotation and visualization.
 *
 * @param namespace - The namespace for the UI annotations.
 * @param annotationPath - The path of the annotation to be analyzed.
 * @param annotationAVT - The annotation object from the AVT (Annotation Vocabulary Tools).
 * @param viewKey - The key to distinguish between different view types.
 * @returns An object containing the visualization, target annotation, and chart title.
 */
function analyzeChartAnnotation(
    namespace: string,
    annotationPath: string,
    annotationAVT: object,
    viewKey: string
): { visualization: string; targetAnnotation: string; chartTitle: string } {
    const visualization = '@UI.Chart';
    const targetAnnotationFullName = `/${namespace}/@${annotationPath}`;
    let chartTitle: string;

    if (annotationAVT['Title']) {
        chartTitle = annotationAVT['Title'];
    } else {
        chartTitle = `Chart View (${viewKey})`;
    }
    return { visualization, targetAnnotation: targetAnnotationFullName, chartTitle };
}

/**
 * Analyzes a SelectionPresentationVariant, to find the right target annotation and visualization.
 *
 * @param uiAnnotations - The UI annotations of the given entity type.
 * @param namespace - The namespace for the UI annotations.
 * @param viewKey - The key to distinguish between different view types.
 * @param visualizationType - The type of visualization to find the right one if there are multiple.
 * @param logger - The logger instance for logging messages.
 * @param title - The title to be displayed for the view.
 * @returns An object containing the visualization, target annotation, namespace, and title.
 */
function analyzeSPVforView(
    uiAnnotations: any,
    namespace: string,
    viewKey: string,
    visualizationType: Visualization,
    logger: ExtensionLogger,
    title: string
): { visualization: string; targetAnnotation: string; namespace: string; title: string } {
    let { visualization, targetAnnotation } = findTheRightVisualization(
        uiAnnotations.PresentationVariant?.Visualizations,
        namespace,
        viewKey,
        visualizationType
    );
    if (!visualization) {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOVISUALIZATIONS')
        });
        visualization = UILINEITEM; // default visualization
        targetAnnotation = `/${namespace}/` + visualization.replace(`UI`, UIVOCABULARY); // default targetAnnotation
    }
    title = title || `View (${viewKey})`;
    return { visualization, targetAnnotation, namespace, title };
}

/**
 * An annotationPath must be analyzed, to find the right target annotation and visualization.
 *
 * @param annotationPath - given annotationPath
 * @param uIAnnotations - annotations of UI namespace of the given entityType
 * @param serviceAVT Combined service metadata, as returned by annotation vocabularies tools.
 * @param viewKey - allows to distinguish the different view types (LR or ALP)
 * @param visualizationType - allows to find the right visualization if there are many.
 * @param logger - extension logger.
 * @returns visualization: string describing the visualization of the annotation,
 *          targetAnnotation: the target annotation behind the visualization
 *          namespace: namespace of the annotation,
 *          title: title to be shown, derived from the annotation
 */
export function analyzeViewAnnotation(
    annotationPath: string,
    uIAnnotations: object,
    serviceAVT: ConvertedMetadata,
    viewKey?: string,
    visualizationType?: Visualization,
    logger?: ExtensionLogger
): { visualization: string; targetAnnotation: string; namespace: string; title: string } {
    let namespace: string = undefined,
        title: string;
    const annotationInManifest = annotationPath?.split(`${UIVOCABULARY}.`)[1];
    const annotationAVT = annotationInManifest && uIAnnotations[annotationInManifest];
    if (!annotationAVT) {
        return { visualization: undefined, targetAnnotation: undefined, namespace, title: `View (${viewKey})` };
    }
    title = annotationAVT.Text;
    namespace = annotationAVT.fullyQualifiedName.split(UIVOCABULARYALPHADOT)[0];

    if (annotationInManifest.startsWith('PresentationVariant')) {
        const { visualization, targetAnnotation } = findTheRightVisualization(
            annotationAVT.Visualizations,
            namespace,
            viewKey,
            visualizationType
        );
        title = title || `View (${viewKey})`;
        return { visualization, targetAnnotation, namespace, title };
    } else if (annotationInManifest.startsWith('SelectionPresentationVariant')) {
        return analyzeSPVforView(annotationAVT, namespace, viewKey, visualizationType, logger, title);
    } else if (annotationInManifest.startsWith('SelectionVariant')) {
        const visualization = UILINEITEM;
        const targetAnnotation = `/${namespace}/@${UIAnnotationTerms.LineItem}`;
        title = title || `Table View (${viewKey})`;
        return { visualization, targetAnnotation, namespace, title };
    } else if (annotationInManifest.startsWith('LineItem')) {
        let visualization = UILINEITEM;
        if (annotationAVT.qualifier) {
            visualization += `#${annotationAVT.qualifier}`;
        }
        const targetAnnotation =
            annotationPath.substring(0, 1) === '@'
                ? `/${namespace}/${annotationPath}`
                : `/${namespace}/@${annotationPath}`;
        title = title || `Table View (${viewKey})`;
        return { visualization, targetAnnotation, namespace, title };
    } else if (annotationInManifest.startsWith('Chart')) {
        const { visualization, targetAnnotation, chartTitle } = analyzeChartAnnotation(
            namespace,
            annotationPath,
            annotationAVT,
            viewKey
        );
        return { visualization, targetAnnotation, namespace, title: chartTitle };
    }
    return { visualization: undefined, targetAnnotation: undefined, namespace, title: `View (${viewKey})` };
}

/**
 * Adds the ALP chart view to the app-specific schema.
 *
 * @param {Definition} appSchema - app-specific JSON schema
 * @param chartAnnotationPath - The annotation path for the chart view
 * @param targetAnnotation - target annotation of the chart view, as determined by analyzeViewAnnotation
 * @param annotations - UI annotations, as determined by AVT
 */
function addALPChartView(appSchema: Definition, chartAnnotationPath: string, targetAnnotation: string, annotations) {
    const chartView = appSchema.definitions[DefinitionName.ALPChartView] as Definition;
    appSchema.definitions[DefinitionName.ALPChart] = JSON.parse(JSON.stringify(chartView));
    delete appSchema.definitions[DefinitionName.ALPChart][SchemaTag.isViewNode];
    if (targetAnnotation) {
        appSchema.definitions[DefinitionName.ALPChart][SchemaTag.annotationPath] = targetAnnotation;
    }
    (appSchema.definitions[DefinitionName.ALPChart] as Definition).properties.annotationPath[SchemaTag.hidden] = true;

    chartView.properties.annotationPath[SchemaTag.hidden] = true;
    chartView.additionalProperties = false;
    chartView.description = `Chart (key: primary)`;
    chartView.title = `Chart`;
    chartView.$ref = `${DEFINITION_LINK_PREFIX}${DefinitionName.ALPChart}`;
    if (chartAnnotationPath) {
        const viewAnnotation = chartAnnotationPath.split(UIVOCABULARYDOT)[1];
        if (annotations[viewAnnotation]) {
            const pathParts = annotations[viewAnnotation].fullyQualifiedName.split('@');
            chartView[SchemaTag.annotationPath] = `/${pathParts[0]}/@${pathParts[1]}`;
        } else {
            // Add message about unexisting annotation path
            addMessageToSchema(chartView, { text: i18next.t('UNEXISTING_ANNOTATION_PATH') });
        }
    }
    chartView[SchemaTag.artifactType] = ArtifactType.Manifest;
    chartView[SchemaTag.key] = 'primary';
    chartView[SchemaTag.keys] = [{ name: SchemaKeyName.key, value: 'primary' }];

    // Apply decorators to the live definition (pass explicitly to avoid deep-copy in AppSchema constructor)
    const alpChartDef = appSchema.definitions[DefinitionName.ALPChart] as Definition;
    new Decorator({ schema: new AppSchema(appSchema) }).init(ALPChart, undefined, alpChartDef);
}

/**
 * Adds the ALP views to the app-specific schema.
 *
 * @param appSchema - app-specific JSON schema
 * @param viewsPaths - views' paths' section in the manifest
 * @param generateParameters - list of API input parameters
 * @param templateAnnotation - defaultTemplateAnnotationPath as registered in manifest
 * @param v4Page - actual page in the manifest
 * @param tableDefinitionName - The name of the table definition in the schema
 */
export function addALPViews(
    appSchema: Definition,
    viewsPaths: Array<object>,
    generateParameters: GenerateAppSchemaParameters,
    templateAnnotation: string,
    v4Page: v4.SapUiAppPageV4,
    tableDefinitionName: string
) {
    const annotations = generateParameters.entityType?.annotations.UI;
    //1. Chart View
    const chartAnnotationPath = viewsPaths?.[0]?.['primary']?.[0]?.[SchemaTag.annotationPath];
    const { targetAnnotation } = analyzeViewAnnotation(
        chartAnnotationPath,
        annotations,
        generateParameters.serviceAVT,
        'primary',
        undefined,
        generateParameters.logger
    );
    addALPChartView(appSchema, chartAnnotationPath, targetAnnotation, annotations);
    // Copy toolbar
    const schemaIdForActions = `Actions<ALPChart>`;
    (appSchema.definitions[DefinitionName.ViewChartToolBar] as Definition).properties.actions['$ref'] =
        DEFINITION_LINK_PREFIX + schemaIdForActions;

    defineActions(appSchema, schemaIdForActions, annotations, targetAnnotation, generateParameters.entityType);

    //2. Table View
    const { visualization: tableVisualization, targetAnnotation: target } = analyzeViewAnnotation(
        viewsPaths?.[0]?.['secondary']?.[0]?.[SchemaTag.annotationPath],
        annotations,
        generateParameters.serviceAVT,
        'secondary',
        undefined,
        generateParameters.logger
    );
    const tableDefinition = appSchema.definitions[tableDefinitionName] as Definition;
    if (tableVisualization || templateAnnotation) {
        tableDefinition.properties.toolBar['$ref'] = `${DEFINITION_LINK_PREFIX}${DefinitionName.ToolBarLR}`;
        // Switch from generic column to specific definition
        tableDefinition.properties['columns'] = {
            $ref: DEFINITION_LINK_PREFIX + DefinitionName.LineItems
        };
        tableDefinition.properties.annotationPath[SchemaTag.hidden] = true;
    } else {
        tableDefinition.properties = {};
    }
    const tableView = appSchema.definitions[DefinitionName.ALPTableView] as Definition;
    appSchema.definitions[DefinitionName.ALPTable] = JSON.parse(JSON.stringify(tableView));
    delete appSchema.definitions[DefinitionName.ALPTable][SchemaTag.isViewNode];
    tableView.additionalProperties = false;
    tableView.description = `Table (key: secondary)`;
    tableView.title = `Table`;
    if (tableVisualization) {
        delete tableView.properties;
        tableView.$ref = `${DEFINITION_LINK_PREFIX}${DefinitionName.ALPTable}`;
    } else if (!templateAnnotation) {
        tableView.properties = {};
    }
    tableView[SchemaTag.artifactType] = ArtifactType.Manifest;

    let annotationTerm: string, viewAnnotation: string;
    if (tableVisualization && tableVisualization.indexOf(FacetBase.LineItem) > -1) {
        annotationTerm = tableVisualization.split('.')[1];
        viewAnnotation = viewsPaths[0]['secondary'][0].annotationPath.split(UIVOCABULARYDOT)[1];
    } else if (templateAnnotation) {
        annotationTerm = viewAnnotation = templateAnnotation.split(UIVOCABULARYDOT)[1];
    }
    if (annotationTerm) {
        const dataForLineItem: LineItemDataForSchema = {
            isObjectPage: false,
            appSchema,
            lineItemAnnotation: generateParameters.entityType?.annotations.UI[
                annotationTerm
            ] as AnnotationTerm<LineItem>,
            entityType: generateParameters.entityType,
            oDataServiceAVT: generateParameters.serviceAVT,
            lineItemId: DefinitionName.LineItems
        };
        addLineItemsTypeToSchema(dataForLineItem, DefinitionName.ViewTableColumn);
        if (target) {
            appSchema.definitions[DefinitionName.ALPTable][SchemaTag.annotationPath] = target;
        }

        const schemaAdjustmentParameters: SchemaAdjustmentParameters = {
            logger: generateParameters.logger,
            appSchema,
            v4Page,
            tableDefinitionKey: DefinitionName.ALPTable,
            facetDefinitionKey: DefinitionName.LineItems,
            dataForLineItem,
            entitySet: generateParameters.entitySet,
            customActionDefinitionName: DefinitionName.CustomTableAction,
            sectionActionsDefinitionKey: `${DefinitionName.Actions}<${prepareRef(DefinitionName.LineItems)}>`
        };
        applyLRSchemaAdjustments(schemaAdjustmentParameters);

        const pathParts = annotations[viewAnnotation].fullyQualifiedName.split('@');
        tableView[SchemaTag.annotationPath] = `/${pathParts[0]}/@${pathParts[1]}`;
        tableView[SchemaTag.key] = 'secondary';
        tableView[SchemaTag.keys] = [{ name: SchemaKeyName.key, value: 'secondary' }];
    }
    addCustomColumnDefinition(appSchema, v4Page, generateParameters.logger);
    // Add "manifestPath" for table properties
    addManifestPathsToPropertiesLR(appSchema, DefinitionName.ALPTable, tableDefinition, {
        pageName: v4Page.id,
        manifest: generateParameters.manifest,
        targetAnnotation: target
    });
}

export type ViewsParameters = {
    viewKey: string;
    appSchema: Definition;
    targetAnnotation: string;
    navPropName: string;
    targetEntityType: EntityType;
    title: string;
    entitySet?: string;
};

/**
 * Modifies the app schema, adds the right line chart settings.
 *
 * @param inputParameters - list of input parameters as collected by the calling instance
 * @param annotations - UI annotations of the given entityType
 * @returns the ID for the chart view in schema
 */
function addViewChartDefinitions(inputParameters: ViewsParameters, annotations): string {
    const idForChart = `${DefinitionName.LRChartView}<${inputParameters.viewKey}>`;
    inputParameters.appSchema.definitions[idForChart] = JSON.parse(
        JSON.stringify(inputParameters.appSchema.definitions[DefinitionName.LRChartView])
    );
    delete inputParameters.appSchema.definitions[idForChart][SchemaTag.isViewNode];
    inputParameters.appSchema.definitions[idForChart][SchemaTag.annotationPath] = inputParameters.targetAnnotation;
    if (inputParameters.navPropName) {
        inputParameters.appSchema.definitions[idForChart]['navigationProperty'] = inputParameters.navPropName;
    }
    //Copy toolbar
    const idForToolbar = `${DefinitionName.ViewChartToolBar}<${inputParameters.viewKey}>`;
    inputParameters.appSchema.definitions[idForToolbar] = JSON.parse(
        JSON.stringify(inputParameters.appSchema.definitions[DefinitionName.ViewChartToolBar])
    );
    inputParameters.appSchema.definitions[idForChart]['properties'].toolBar.$ref =
        `${DEFINITION_LINK_PREFIX}${idForToolbar}`;
    //Copy actions
    const schemaIdForActions = `${DefinitionName.Actions}<${inputParameters.viewKey}>`;
    defineActions(
        inputParameters.appSchema,
        schemaIdForActions,
        annotations,
        inputParameters.targetAnnotation,
        inputParameters.targetEntityType
    );
    inputParameters.appSchema.definitions[idForToolbar]['properties'][PropertyName.actions]['$ref'] =
        `${DEFINITION_LINK_PREFIX}${schemaIdForActions}`;

    return idForChart;
}

/**
 * Adds the definition of a chart view to the app schema.
 *
 * @param inputParameters - list of input parameters as collected by the calling instance
 * @param UIAnnotations - AVT UI annotations of the given entity type
 * @param appSchema - app schema
 * @param viewsDefinition - the views definition in app schema (MultiTableModeV4)
 * @param viewKey - view key
 * @param title - view title
 * @param page - Represents the page in the application's structure where the view line items are to be added.
 * @param app - Represents the overall application structure, providing access to the manifest.
 */
function addViewChart(
    inputParameters: ViewsParameters,
    UIAnnotations,
    appSchema: Definition,
    viewsDefinition: Definition,
    viewKey: string,
    title: string,
    page: Page,
    app: App
) {
    const idForChart = addViewChartDefinitions(inputParameters, UIAnnotations);
    const chartViewDefinition = appSchema.definitions[idForChart] as Definition;
    chartViewDefinition.properties.annotationPath[SchemaTag.hidden] = true;
    chartViewDefinition.properties.index[SchemaTag.hidden] = true;
    chartViewDefinition.properties.entitySet[SchemaTag.hidden] = true;
    const titleOnHover = `Chart View (key: ${viewKey})`;
    viewsDefinition.properties[viewKey] = {
        $ref: `${DEFINITION_LINK_PREFIX}${idForChart}`,
        description: title ? title : titleOnHover,
        title: titleOnHover
    };
    // Add "manifestPath" for table properties
    addManifestPathsToPropertiesLR(
        inputParameters.appSchema,
        DefinitionName.LRChartView,
        chartViewDefinition,
        {
            pageName: page.target.id,
            manifest: app.manifest,
            targetAnnotation: inputParameters.targetAnnotation,
            viewConfig: {
                key: viewKey,
                entitySet: inputParameters.entitySet
            }
        },
        true
    );
}

/**
 * Retrieves the line item reference for a specified view and entity type.
 *
 * @param {SapUiAppPageV4} v4Page - The page object containing application-specific settings and views configuration.
 * @param {EntityType} entityType - The entity type definition for which the line item reference is required.
 * @param {string} viewKey - The key identifying the desired view configuration.
 * @param {string} lineItemTerm - The term representing the line item annotation.
 * @param {string} lineItemQualifier - The qualifier representing the line item annotation.
 * @returns {string} The resolved line item reference in the form of a path or annotation term.
 */
function getLineItemRef(
    v4Page: SapUiAppPageV4,
    entityType: EntityType,
    viewKey: string,
    lineItemTerm: string,
    lineItemQualifier?: string
): string {
    const qualifier = lineItemQualifier ? `#${lineItemQualifier}` : ``;
    const path = ((v4Page?.options?.settings?.views?.paths as SapUi5MultiViewPaths) ?? []).find(
        (path) => path.key === viewKey
    );
    if (path === undefined) {
        return `@${lineItemTerm}${qualifier}`;
    }
    const { annotationPath, entitySet } = path;
    const viewAnnotationPath = (annotationPath ?? '').split(UIVOCABULARYDOT)[1];
    const lineItemReference =
        entityType?.annotations?.UI?.[viewAnnotationPath]?.PresentationVariant?.Visualizations?.[0]?.value ??
        `@${lineItemTerm}${qualifier}`;

    return entitySet ? `/${entitySet}/${lineItemReference}` : lineItemReference;
}

/**
 * Adjusts the schema for a table by making necessary changes such as populating creation fields,
 * adjusting creation field definitions, and adding enum mappings for mass edit functionalities.
 *
 * @param {SchemaAdjustmentParameters} schemaAdjustmentParameters - Object containing the parameters required for schema adjustments. Includes the generate parameters, application schema, table definition ID, and facet definition ID.
 */
export function applyLRSchemaAdjustments(schemaAdjustmentParameters: SchemaAdjustmentParameters) {
    const { entityType, appSchema, tableDefinitionKey, facetDefinitionKey } = schemaAdjustmentParameters;

    // in the future, all postgeneration adjustments for the LR Page should be done here

    // populate the creation fields for the table with the data from the columns
    initializeCreationModeSchema(appSchema, facetDefinitionKey, entityType, DefinitionName.TableCreationModeLR);
    // Apply decorator-based visibility rules for table properties
    applyTableDecorators(schemaAdjustmentParameters);
    // add columns to a enum, in this case enableMassEdit -> visibleFields and ignoredFields
    addEnumForEnableMassEdit(appSchema, tableDefinitionKey, facetDefinitionKey, entityType);

    // add the custom action and custom action menu definition
    configureCustomActionDefinition(schemaAdjustmentParameters);
}

/**
 * Retrieves the table settings from the manifest for a given line item reference.
 * The returned object includes both TableSettings and Table-level properties
 * like creationMode, analyticalConfiguration, and popinLayout.
 *
 * @param v4Page - The V4 page configuration from manifest
 * @param lineItemRef - The line item reference (e.g., '@UI.LineItem')
 * @returns The table settings or undefined if not found
 */
function getTableSettingsFromManifest(
    v4Page: v4.SapUiAppPageV4,
    lineItemRef: string
): (v4.TableSettings & Partial<v4.Table>) | undefined {
    return v4Page?.options?.settings?.controlConfiguration?.[lineItemRef ?? '']?.[PropertyName.tableSettings];
}

/**
 * Applies decorator-based visibility rules to table and creation mode definitions.
 * This function uses the @hide and @message decorators defined on the Table and TableCreationModeLR classes
 * to conditionally hide properties and show warning messages based on table type and settings.
 *
 * @param schemaAdjustmentParameters - Parameters containing schema, page, and table definition information
 */
function applyTableDecorators(schemaAdjustmentParameters: SchemaAdjustmentParameters) {
    const { appSchema, tableDefinitionKey, facetDefinitionKey, v4Page, viewKey, entityType, dataForLineItem } =
        schemaAdjustmentParameters;

    // Get the line item reference for looking up table settings in manifest
    const lineItemRef = getLineItemRef(
        v4Page,
        entityType,
        viewKey,
        entityType?.annotations?.UI?.LineItem?.term,
        dataForLineItem?.lineItemAnnotation?.qualifier
    );

    // Get table settings from manifest and build custom context for decorator evaluation
    const tableSettings = getTableSettingsFromManifest(v4Page, lineItemRef);

    // Check InsertRestrictions to determine if entity/navigation allows insertion
    const { entitySet, navigationPath } = schemaAdjustmentParameters;
    const isInsertable = getInsertableForEntitySet(entitySet, navigationPath);

    const customContext = {
        ...tableSettings,
        type: tableSettings?.type ?? DEFAULT_TABLE_TYPE,
        isInsertable
    };

    // Get the table definition to apply decorators to
    const tableDefinition = appSchema.definitions[tableDefinitionKey] as Definition;

    // Get the creation mode definition
    const creationModeDefinitionKey = getFacetDefinitionKey(DefinitionName.TableCreationModeLR, facetDefinitionKey);
    const creationModeDefinition =
        (appSchema.definitions[creationModeDefinitionKey] as Definition) ??
        parseSchemaDefinition(DefinitionName.TableCreationModeLR, facetDefinitionKey, appSchema);

    // Set up the $ref for creationMode property
    if (tableDefinition?.properties?.[PropertyName.creationMode]) {
        const creationModeProperty = tableDefinition.properties[PropertyName.creationMode] as Definition;
        creationModeProperty.$ref = getFacetDefinitionLink(DefinitionName.TableCreationModeLR, facetDefinitionKey);
    }

    // Apply decorators to the Table definition (creationMode, analyticalConfiguration, popinLayout)
    const decorator = new Decorator({ schema: new AppSchema(appSchema) });
    decorator.init(Table, customContext, tableDefinition);

    // Apply decorators to the TableCreationModeLR definition (creationFields, createInPlace, isCreateEnabled)
    decorator.init(TableCreationModeLR, customContext, creationModeDefinition);

    // Apply analyticalConfiguration decorators only when the property is specified in manifest
    const hasAnalyticalConfiguration = tableSettings?.analyticalConfiguration !== undefined;
    if (!hasAnalyticalConfiguration) {
        return;
    }

    if (viewKey) {
        // Multi-view: create a view-specific definition to isolate decorator messages per view
        parseSchemaDefinition(DefinitionName.ViewAnalyticalConfiguration, viewKey, appSchema);
        const definitionKey = getFacetDefinitionKey(DefinitionName.ViewAnalyticalConfiguration, viewKey);
        const definition = appSchema.definitions[definitionKey] as Definition;

        if (definition) {
            decorator.init(ViewAnalyticalConfiguration, customContext, definition);

            // Update the table's $ref to point to the view-specific definition
            const analyticalConfigProp = tableDefinition?.properties?.[PropertyName.analyticalConfiguration];
            if (analyticalConfigProp) {
                (analyticalConfigProp as Definition).$ref = getFacetDefinitionLink(
                    DefinitionName.ViewAnalyticalConfiguration,
                    viewKey
                );
            }
        }
    } else {
        // Single table: use the shared definition
        const definition = appSchema.definitions[DefinitionName.AnalyticalConfiguration] as Definition;
        if (definition) {
            decorator.init(AnalyticalConfiguration, customContext, definition);
        }
    }
}

/**
 * Modifies the app schema, adds the right line item settings as derived from the visualization.
 *
 * @param {ViewsParameters} inputParameters - Parameters containing details needed for the view line item addition, including the app schema, view key, and target annotation.
 * @param {Definition} viewsDefinition - The definition object to which the view line items will be added.
 * @param {string} visualization - The specific visualization type, typically used to determine the annotation term.
 * @param {string} templateAnnotation - Annotation path or type related to the template.
 * @param generateParameters - list of API input parameters
 * @param generateParameters.serviceAVT - OData service AVT information
 * @param generateParameters.entityType - entity type of the current page
 * @param generateParameters.entitySet - The specified entity set for the view.
 * @param {Page} page - Represents the page in the application's structure where the view line items are to be added.
 * @param app - the application object containing manifest and other app-specific information.
 * @param {ExtensionLogger} logger - Logger for recording informational or error messages during the schema adjustment process.
 */
function addViewLineItems(
    inputParameters: ViewsParameters,
    viewsDefinition: Definition,
    visualization: string,
    templateAnnotation: string,
    { serviceAVT, entityType, entitySet }: Partial<GenerateAppSchemaParameters>,
    page: Page,
    app: App,
    logger: ExtensionLogger
): void {
    const tableId = `${DefinitionName.LRTableView}<${inputParameters.viewKey}>`;
    const schemaIdForColumns = `${DefinitionName.LineItemsOfView}::${inputParameters.viewKey}`;
    const schemaIdForActions = `${DefinitionName.Actions}<${schemaIdForColumns}>`;
    const lrTableView = parseSchemaDefinition(
        DefinitionName.LRTableView,
        inputParameters.viewKey,
        inputParameters.appSchema
    );
    delete lrTableView[SchemaTag.isViewNode];
    lrTableView[SchemaTag.annotationPath] = inputParameters.targetAnnotation;
    if (inputParameters.navPropName) {
        lrTableView['navigationProperty'] = inputParameters.navPropName;
    }
    if (inputParameters.entitySet) {
        lrTableView['entitySet'] = inputParameters.entitySet;
    }
    //Copy toolbar
    addDefinitionRef(lrTableView.properties.toolBar as Definition, DefinitionName.ToolBar, schemaIdForColumns);
    const title = `Table View (key: ${inputParameters.viewKey})`;
    viewsDefinition.properties[inputParameters.viewKey] = {
        $ref: `${DEFINITION_LINK_PREFIX}${tableId}`,
        description: inputParameters.title || title,
        title
    };
    addDefinitionRef(lrTableView.properties.columns as Definition, schemaIdForColumns);
    lrTableView.properties.annotationPath[SchemaTag.hidden] = true;
    lrTableView.properties.index[SchemaTag.hidden] = true;
    lrTableView.properties.entitySet[SchemaTag.hidden] = true;
    const annotationTerm = visualization?.split('.')[1] || templateAnnotation.split(UIVOCABULARYDOT)[1];
    const dataForLineItem: LineItemDataForSchema = {
        isObjectPage: false,
        appSchema: inputParameters.appSchema,
        lineItemAnnotation: inputParameters.targetEntityType?.annotations.UI[
            annotationTerm
        ] as AnnotationTerm<LineItem>,
        entityType: inputParameters.targetEntityType,
        oDataServiceAVT: serviceAVT,
        lineItemId: schemaIdForColumns
    };
    addLineItemsTypeToSchema(
        dataForLineItem,
        DefinitionName.ViewTableColumn,
        DefinitionName.ViewCustomColumn,
        DefinitionName.ViewCustomAction
    );

    // Table toolbar actions with custom actions
    const sectionIdPrefix = inputParameters.entitySet ? `/${inputParameters.entitySet}/` : '';
    const sectionIdInManifest = `${sectionIdPrefix}@${UIAnnotationTerms.LineItem}${
        visualization.split(Visualization.LineItem)[1]
    }`;
    const customActionDefinitionKey = `${DefinitionName.ViewCustomAction}<${schemaIdForColumns}>`;
    // Anchor selection
    parseSchemaDefinition(DefinitionName.ViewCustomActionPosition, schemaIdForColumns, inputParameters.appSchema);
    const customActionPositionDefinitionKey = `${DefinitionName.ViewCustomActionPosition}<${schemaIdForColumns}>`;
    const customActionDefinition = inputParameters.appSchema.definitions[customActionDefinitionKey] as Definition;
    (customActionDefinition.properties.position as Definition).$ref =
        `${DEFINITION_LINK_PREFIX}${customActionPositionDefinitionKey}`;

    const schemaAdjustmentParameters: SchemaAdjustmentParameters = {
        logger: logger,
        appSchema: inputParameters.appSchema,
        v4Page: page.target,
        tableDefinitionKey: tableId,
        facetDefinitionKey: schemaIdForColumns,
        viewKey: inputParameters.viewKey,
        entityType: inputParameters.targetEntityType || entityType,
        entitySet,
        navigationPath: inputParameters.navPropName,
        dataForLineItem,
        sectionActionsDefinitionKey: schemaIdForActions,
        scopedCustomActionDefinitionKey: customActionDefinitionKey,
        customActionDefinitionName: DefinitionName.ViewCustomAction,
        sectionIdInManifest,
        customActionPosition: customActionPositionDefinitionKey
    };
    applyLRSchemaAdjustments(schemaAdjustmentParameters);
    // Add "manifestPath" for table properties
    addManifestPathsToPropertiesLR(
        inputParameters.appSchema,
        DefinitionName.LRTableView,
        lrTableView,
        {
            pageName: page.target.id,
            manifest: app.manifest,
            targetAnnotation: inputParameters.targetAnnotation,
            viewConfig: {
                key: inputParameters.viewKey,
                entitySet: inputParameters.entitySet
            }
        },
        true
    );
    // Add "manifestPath" for view actions
    addManifestPathsToDefinitionPropertiesLR(
        inputParameters.appSchema,
        page.target.id,
        schemaIdForActions,
        app.getManifest(),
        inputParameters.targetAnnotation,
        inputParameters.entitySet
    );
}

/**
 * Adds the schema tags to a given view.
 *
 * @param viewsDefinition - the views definition in app schema (MultiTableModeV4)
 * @param viewKey - key property of the view
 * @param namespace - target namespace for annotations
 * @param view - the given view
 */
export function addTagsToView(viewsDefinition: Definition, viewKey: string, namespace: string, view: object) {
    viewsDefinition.properties[viewKey][SchemaTag.isViewNode] = true;
    viewsDefinition.properties[viewKey][SchemaTag.annotationPath] = namespace
        ? `/${namespace}/@${view[SchemaTag.annotationPath]}`
        : undefined;
    viewsDefinition.properties[viewKey][SchemaTag.artifactType] = ArtifactType.Manifest;
    viewsDefinition.properties[viewKey][SchemaTag.key] = viewKey;
    viewsDefinition.properties[viewKey][SchemaTag.keys] = [{ name: SchemaKeyName.key, value: viewKey }];
}

/**
 * Adds view custom extensions - custom column and custom action definitions.
 *
 * @param commonInputParameters - list of input parameters as collected by the calling instance
 * @param visualization - visualization of the annotation
 * @param v4Page - the given page in manifest
 * @param manifest - The application manifest
 * @param logger - extension logger
 */
function addViewCustomExtensions(
    commonInputParameters: ViewsParameters,
    visualization: string,
    v4Page: v4.SapUiAppPageV4,
    manifest: Manifest,
    logger: ExtensionLogger
) {
    const schemaIdForColumns = `${DefinitionName.LineItemsOfView}::${commonInputParameters.viewKey}`;
    const sectionIdPrefix = commonInputParameters.entitySet ? `/${commonInputParameters.entitySet}/` : '';
    const sectionIdInManifest = `${sectionIdPrefix}@${UIAnnotationTerms.LineItem}${
        visualization.split(Visualization.LineItem)[1]
    }`;
    const viewColumnDefinition = parseSchemaDefinition(
        DefinitionName.ViewCustomColumn,
        schemaIdForColumns,
        commonInputParameters.appSchema
    );
    addDefinitionRef(
        viewColumnDefinition.properties.position as Definition,
        DefinitionName.ViewPosition,
        schemaIdForColumns
    );
    parseSchemaDefinition(DefinitionName.ViewPosition, schemaIdForColumns, commonInputParameters.appSchema);
    addCustomColumnDefinition(
        commonInputParameters.appSchema,
        v4Page,
        logger,
        `${DefinitionName.ViewCustomColumn}<${schemaIdForColumns}>`,
        schemaIdForColumns,
        sectionIdInManifest
    );
    addFragmentEnumForAnchor(
        commonInputParameters.appSchema,
        schemaIdForColumns,
        v4Page,
        `${DefinitionName.ViewPosition}<${schemaIdForColumns}>` as DefinitionName,
        sectionIdInManifest
    );
    // Add "manifestPath" for view columns
    addManifestPathsToDefinitionPropertiesLR(
        commonInputParameters.appSchema,
        v4Page.id,
        schemaIdForColumns,
        manifest,
        commonInputParameters.targetAnnotation,
        commonInputParameters.entitySet
    );
    // Remove generic position
    if (commonInputParameters.appSchema.definitions.Position) {
        delete commonInputParameters.appSchema.definitions.Position;
    }
}

/**
 * Adds definition parts to a LR View (LineItem/Chart).
 *
 * @param visualization - visualization of the annotation
 * @param viewsDefinition - the views definition in app schema (MultiTableModeV4)
 * @param commonInputParameters - list of input parameters as collected by the calling instance
 * @param generateParameters - list of API input parameters
 * @param generateParameters.serviceAVT - OData service AVT information
 * @param generateParameters.entityType - entity type of the current page
 * @param generateParameters.logger - extension logger
 * @param page - the current page object
 * @param app - the application object containing manifest and other app-specific information.
 * @param uiAnnotations - UI annotations of the given entityType
 * @param view - the given view
 */
export function addLRViewParts(
    visualization: string,
    viewsDefinition: Definition,
    commonInputParameters: ViewsParameters,
    { serviceAVT, entityType, logger }: Partial<GenerateAppSchemaParameters>,
    page: Page,
    app: App,
    uiAnnotations: object,
    view: object
) {
    if (visualization?.indexOf(Visualization.LineItem) > -1) {
        //Add Line item view
        addViewLineItems(
            commonInputParameters,
            viewsDefinition,
            visualization,
            commonInputParameters.targetAnnotation,
            { serviceAVT, entityType },
            page,
            app,
            logger
        );
        addViewCustomExtensions(commonInputParameters, visualization, page.target, app.getManifest(), logger);
    } else if (visualization?.indexOf('Chart') > -1) {
        //Add Chart view
        addViewChart(
            commonInputParameters,
            uiAnnotations,
            commonInputParameters.appSchema,
            viewsDefinition,
            commonInputParameters.viewKey,
            commonInputParameters.title,
            page,
            app
        );
    } else if (view['template']) {
        const titleOnHover = view['label'] || `Custom View (key: ${commonInputParameters.viewKey})`;
        viewsDefinition.properties[commonInputParameters.viewKey] = {
            description: commonInputParameters.title || titleOnHover,
            title: titleOnHover,
            // Default view - use table view reference as default
            $ref: `${DEFINITION_LINK_PREFIX}${DefinitionName.TableViewExtension}`
        };
    } else {
        const titleOnHover = `View (key: ${commonInputParameters.viewKey})`;
        viewsDefinition.properties[commonInputParameters.viewKey] = {
            description: commonInputParameters.title || titleOnHover,
            title: titleOnHover,
            // Default view - use table view reference as default
            $ref: `${DEFINITION_LINK_PREFIX}${DefinitionName.LRTableView}`
        };
    }
}

/**
 * Checks for the default annotation path to use for LR table.
 *
 * @param page - the current page object
 * @returns annotation term
 */
function getDefaultAnnotationPath(page: Page): string | undefined {
    const {
        entity: { type: entityType },
        template: { type: templateType },
        target
    } = page;
    if (target?.options?.settings?.views?.paths && templateType === TemplateType.ListReportObjectPageV4) {
        return;
    }
    if (target?.options?.settings?.defaultTemplateAnnotationPath) {
        return target?.options?.settings.defaultTemplateAnnotationPath;
    }
    if (templateType === TemplateType.ListReportObjectPageV4) {
        return (
            entityType?.annotations.UI?.SelectionPresentationVariant?.term ||
            entityType?.annotations.UI?.PresentationVariant?.term
        );
    }
}

/**
 * Adds header actions to schema (LR or ALP).
 *
 * @param appSchema - app-specific JSON schema
 * @param v4Page - actual page in the manifest
 * @param logger - Logger class for logging messages
 */
export function addHeaderActions(appSchema: Definition, v4Page: v4.SapUiAppPageV4, logger: ExtensionLogger): void {
    // add header action definitions
    const headerActionsDefinitionKey = DefinitionName.HeaderActions;
    const headerActionsDefinition = appSchema.definitions[headerActionsDefinitionKey] as Definition;
    headerActionsDefinition[SchemaTag.isViewNode] = true;
    headerActionsDefinition.description = 'Actions';
    headerActionsDefinition.properties = {};

    // add custom action definitions
    const customActionDefinitionKey = DefinitionName.CustomHeaderAction;
    const customActionDefinition = appSchema.definitions[customActionDefinitionKey] as Definition;
    customActionDefinition[SchemaTag.isViewNode] = true;
    customActionDefinition.description = 'Custom Action';
    addHeaderFooterCustomActionDefinition(
        appSchema,
        v4Page,
        logger,
        customActionDefinitionKey,
        headerActionsDefinitionKey
    );
    const facetKey = DefinitionName.CustomHeaderActionPosition;
    parseSchemaDefinition(facetKey, facetKey, appSchema, false);

    // Anchor selection
    (customActionDefinition.properties.position as Definition).$ref = `${DEFINITION_LINK_PREFIX}${facetKey}`;
    addEnumForActionAnchor(appSchema, headerActionsDefinitionKey, facetKey);
}

/**
 * Adds 'manifestPath' to each property in a definition based on metadata sync rules.
 *
 * @param appSchema - Full schema.
 * @param definitionName - The name of the definition/class whose properties are being processed.
 * @param definition - The definition object containing property schemas.
 * @param pathParams - Additional context required to compute manifest paths.
 * @param handleRelative - A flag indicating whether to use relative paths for definition properties.
 */
export function addManifestPathsToPropertiesLR(
    appSchema: Definition,
    definitionName: string | undefined,
    definition: Definition,
    pathParams: ManifestPathParams,
    handleRelative = false
): void {
    let targetAnnotation: string | undefined = undefined;
    if (pathParams.targetAnnotation) {
        const annotationParts = pathParams.targetAnnotation?.split('/');
        targetAnnotation = annotationParts[annotationParts.length - 1];
    } else {
        // Default annotation path
        targetAnnotation = `@${UIAnnotationTerms.LineItem}`;
    }
    const entitySet = pathParams.viewConfig?.entitySet;
    if (entitySet) {
        targetAnnotation = entitySet ? `/${entitySet}/${targetAnnotation}` : targetAnnotation;
    }
    addManifestPathsToProperties(
        appSchema,
        definitionName,
        definition,
        {
            ...pathParams,
            pathsParts: [
                pathParams.sectionId ? undefined : targetAnnotation,
                pathParams.columnKey ? pathParams.columnKey : '',
                targetAnnotation,
                pathParams.viewConfig
            ]
        },
        handleRelative
    );
}

/**
 * Adds 'manifestPath' to static nodes in schema.
 *
 * @param appSchema - Full schema.
 * @param pathParams - Additional context required to compute manifest paths.
 */
function addManifestPathsToStaticNodes(appSchema: Definition, pathParams: ManifestPathParams): void {
    // Add "manifestPath" for current page
    appSchema['manifestPath'] = getPageManifestPath(pathParams);
    // Root level - rules stored on page class level
    addManifestPathsToPropertiesLR(appSchema, PageTypeV4.ListReport, appSchema, pathParams);
    // Other definitions
    const definitions = [DefinitionName.FilterBar, DefinitionName.MultiTableModeV4];
    for (const definitionName of definitions) {
        const definition = appSchema.definitions?.[definitionName];
        if (typeof definition === 'object') {
            addManifestPathsToPropertiesLR(appSchema, definitionName, definition, pathParams);
        }
    }
}

/**
 * Class to generate the List Report page schema.
 */
class ListReportPage {
    app: App;
    page: Page;
    appSchema: AppSchema;
    logger?: ExtensionLogger;
    /**
     * Constructor for ListReportPage.
     *
     * @param generateParameters - parameters for generating the app schema
     * @param schema - generic JSON schema of the List Report page
     * @param templateName - name of the template
     * @param contextPath - context path of the page
     */
    constructor(
        generateParameters: GenerateAppSchemaParameters,
        schema: object,
        templateName: string,
        contextPath: string
    ) {
        const template = { name: templateName, type: generateParameters.templateType };
        this.appSchema = new AppSchema(schema);
        this.app = createApp(generateParameters.manifest, generateParameters.serviceAVT, generateParameters.logger);
        this.page = new Page(
            this.app,
            template,
            generateParameters.entitySet,
            generateParameters.entityType,
            contextPath
        );
        this.logger = this.app.logger;
        this.page.addConfig({
            isALP: template.type === TemplateType.AnalyticalListPageV4,
            views: this.page.target?.options?.settings?.views,
            defaultAnnotation: getDefaultAnnotationPath(this.page)
        });
    }

    /**
     * Cleans up the schema by hiding specific properties and removing unnecessary definitions.
     */
    cleanup(): void {
        this.appSchema.hide(this.appSchema.getRootProperty([PropertyName.defaultTemplateAnnotationPath]));
        this.appSchema.cleanup(['GenericColumns', 'Action']);
        updatePropertyIndices(this.appSchema.get(), ROOT_PROPERTIES_ORDER);
    }

    /**
     * Generates the finalized schema for the List Report page.
     *
     * @returns The finalized schema for List Report page
     */
    generate(): Definition {
        const params: DecoratorContext = {
            schema: this.appSchema,
            app: this.app,
            page: this.page,
            logger: this.logger
        };

        const decorator = new Decorator({
            schema: this.appSchema,
            app: this.app,
            page: this.page,
            logger: this.logger
        });

        new GeneralListReportSettings(params).init();
        if (!this.page.target) {
            return this.appSchema.get();
        }

        decorator.init([Header, HeaderActions, CustomHeaderAction]);
        new FilterBar(params).init();
        new TableAndViews(params).init();

        // Add "manifestPath" for properties in static nodes
        addManifestPathsToStaticNodes(this.appSchema.get(), {
            pageName: this.page.target.id,
            manifest: this.app.getManifest()
        });

        // Add "manifestPath" for custom header actions
        const definitionNames = [DefinitionName.HeaderActions];
        definitionNames.forEach((definitionName: string) => {
            addManifestPathsToDefinitionPropertiesLR(
                this.appSchema.get(),
                this.page.target.id,
                definitionName,
                this.app.getManifest()
            );
        });

        this.cleanup();
        return this.appSchema.get();
    }
}

/**
 * Generates an app specific schema for the FE V4 ListReport from the generic schema.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param genericSchema  - generic JSON schema of an object page
 * @param {string} templateName - allows to distinguish list report from ALP
 * @param {string} contextPath - entity set (AVT object) that is relevant for the given page
 * @returns {object} application-specific schema of the list report
 */
export function generateListReportSchemaV4(
    generateParameters: GenerateAppSchemaParameters,
    genericSchema: object,
    templateName: string,
    contextPath: string
): object {
    const listReport = new ListReportPage(generateParameters, genericSchema, templateName, contextPath);
    if (!listReport.app.getManifest()) {
        listReport.cleanup();
        return listReport.appSchema.get();
    }
    return listReport.generate();
}
